/**
 * Validate Tunisian Matricule Fiscal.
 * Canonical form: 7 digits + up to 3 letters + optional 3 digits (établissement).
 * Accepted (after stripping spaces, slashes, dashes):
 *   - 1873400G            (short, 7 digits + TVA category letter)
 *   - 1873400GM           (+ contributor type)
 *   - 1873400GMC          (+ secondary category)
 *   - 1873400GMC000       (+ établissement number)
 */
export function validateMatriculeFiscal(mf: string): boolean {
  if (!mf) return false;
  const cleaned = mf.replace(/[\s/-]/g, '');
  const regex = /^\d{7}[A-Za-z]{1,3}(\d{3})?$/;
  return regex.test(cleaned);
}

/**
 * Validate Tunisian RIB (Relevé d'Identité Bancaire)
 * Format: 20 digits (2 bank + 3 branch + 13 account + 2 key)
 */
export function validateRIB(rib: string): boolean {
  if (!rib) return false;
  const cleaned = rib.replace(/[\s-]/g, '');
  return /^\d{20}$/.test(cleaned);
}

/**
 * Validate email address
 */
export function validateEmail(email: string): boolean {
  if (!email) return true; // optional
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

/**
 * Validate phone number (Tunisian format)
 */
export function validatePhone(phone: string): boolean {
  if (!phone) return true; // optional
  const cleaned = phone.replace(/[\s\-+()]/g, '');
  return /^(\+?216)?\d{8}$/.test(cleaned);
}

/**
 * Validate postal code (Tunisian: 4 digits)
 */
export function validateCodePostal(cp: string): boolean {
  if (!cp) return true; // optional
  return /^\d{4}$/.test(cp.trim());
}

/**
 * Required field validation
 */
export function required(value: string | number | undefined | null): string | undefined {
  if (value === undefined || value === null || value === '') {
    return 'Ce champ est obligatoire';
  }
  return undefined;
}

/**
 * Min length validation
 */
export function minLength(min: number) {
  return (value: string): string | undefined => {
    if (value && value.length < min) {
      return `Minimum ${min} caractères`;
    }
    return undefined;
  };
}

/**
 * Positive number validation
 */
export function positiveNumber(value: number | string): string | undefined {
  const num = typeof value === 'string' ? parseFloat(value) : value;
  if (isNaN(num) || num < 0) {
    return 'La valeur doit être positive';
  }
  return undefined;
}
